import { tradeRecords } from '@/mocks/tradingData';
import { STARTING_BALANCE } from '@/utils/accountCurve';

const formatSigned = (n: number): string =>
  `${n < 0 ? '-' : '+'}${Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatMoney = (n: number): string =>
  `$${n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatPercent = (value: number): string => `${value.toFixed(1)}%`;

// Single source of truth for every headline figure on the site.
//
// All counts, rates, best/worst trades and streak figures are derived from the
// one trade ledger (`tradeRecords`). Edit that ledger and every page — home,
// gold strategy, how it works, trade journal — updates together, so the numbers
// can never drift out of sync again.
const deriveTradingSummary = () => {
  const totalTrades = tradeRecords.length;
  const profits = tradeRecords.map((t) => t.profit);

  const winTrades = profits.filter((p) => p > 0).length;
  const lossTrades = totalTrades - winTrades;
  const longTrades = tradeRecords.filter((t) => t.side === 'BUY').length;
  const shortTrades = totalTrades - longTrades;

  let best = -Infinity;
  let worst = Infinity;
  let maxWinRun = 0;
  let maxLossRun = 0;
  let winRun = 0;
  let lossRun = 0;
  let winRunSum = 0;
  let lossRunSum = 0;
  let bestWinSum = 0;
  let worstLossSum = 0;

  tradeRecords.forEach(({ profit }) => {
    if (profit > 0) {
      winRun += 1;
      winRunSum += profit;
      lossRun = 0;
      lossRunSum = 0;
      maxWinRun = Math.max(maxWinRun, winRun);
      bestWinSum = Math.max(bestWinSum, winRunSum);
    } else {
      lossRun += 1;
      lossRunSum += profit;
      winRun = 0;
      winRunSum = 0;
      maxLossRun = Math.max(maxLossRun, lossRun);
      worstLossSum = Math.min(worstLossSum, lossRunSum);
    }
    best = Math.max(best, profit);
    worst = Math.min(worst, profit);
  });

  const first = tradeRecords[0];
  const last = tradeRecords[totalTrades - 1];

  // Cumulative result of the whole ledger and the account balance it leaves
  // behind. Both are pure sums over `tradeRecords`, so a new trade moves them
  // automatically — nothing here is typed by hand.
  const netProfitValue = Number(profits.reduce((sum, p) => sum + p, 0).toFixed(2));
  const endingBalanceValue = Number((STARTING_BALANCE + netProfitValue).toFixed(2));

  const winRateValue = (winTrades / totalTrades) * 100;
  const lossRateValue = (lossTrades / totalTrades) * 100;
  const longPercent = (longTrades / totalTrades) * 100;
  const shortPercent = (shortTrades / totalTrades) * 100;

  // Per-direction win stats — a global win rate must never be shown as if it
  // belonged to one side. Long = BUY, Short = SELL.
  const longWinTrades = tradeRecords.filter((t) => t.side === 'BUY' && t.profit > 0).length;
  const shortWinTrades = tradeRecords.filter((t) => t.side === 'SELL' && t.profit > 0).length;
  const longWinRateValue = longTrades > 0 ? (longWinTrades / longTrades) * 100 : 0;
  const shortWinRateValue = shortTrades > 0 ? (shortWinTrades / shortTrades) * 100 : 0;

  // Best result achieved on each side of the record, so per-direction widgets
  // (e.g. the home MT5 window) can quote a real ledger figure instead of a
  // hand-typed one that would drift the moment the ledger changes.
  const longProfits = tradeRecords.filter((t) => t.side === 'BUY').map((t) => t.profit);
  const shortProfits = tradeRecords.filter((t) => t.side === 'SELL').map((t) => t.profit);
  const bestLong = longProfits.length > 0 ? Math.max(...longProfits) : 0;
  const bestShort = shortProfits.length > 0 ? Math.max(...shortProfits) : 0;

  return {
    period: `${first.date} — ${last.date}`,
    instrument: 'XAUUSD',
    totalTrades,
    winTrades,
    lossTrades,
    winRate: formatPercent(winRateValue),
    lossRate: formatPercent(lossRateValue),
    winRateValue,
    lossRateValue,
    longTrades,
    shortTrades,
    longPercent,
    shortPercent,
    longWinTrades,
    shortWinTrades,
    longWinRate: formatPercent(longWinRateValue),
    shortWinRate: formatPercent(shortWinRateValue),
    longWinRateValue,
    shortWinRateValue,
    instrumentShare: '100%',
    bestTrade: formatSigned(best),
    worstTrade: formatSigned(worst),
    bestLongTrade: formatSigned(bestLong),
    bestShortTrade: formatSigned(bestShort),
    maxConsecutiveWins: maxWinRun,
    maxConsecutiveLosses: maxLossRun,
    maxConsecutiveProfit: formatSigned(bestWinSum),
    maxConsecutiveLoss: formatSigned(worstLossSum),
    netProfit: formatMoney(netProfitValue),
    netProfitValue,
    endingBalance: formatMoney(endingBalanceValue),
    endingBalanceValue,
    startingBalance: STARTING_BALANCE,
    startingBalanceText: formatMoney(STARTING_BALANCE),
    // Year label for the record panels — read off the newest trade so it moves
    // forward on its own the moment a trade in a new year is added to the ledger.
    latestYear: String(new Date(last.date).getFullYear()),
  };
};

export const tradingSummary = deriveTradingSummary();